import { Auth0Provider } from '@auth0/auth0-react';
import { FC, ReactNode } from 'react';
import { useAuthRedirect } from '../hooks';
import { getAuth0Config } from '../constants';
import { Auth0Config } from '../types';

/**
 * Props for Auth0ProviderWithHistory component
 */
export interface Auth0ProviderWithHistoryProps {
    children: ReactNode;
    router: {
        navigate: (path: string, options?: { replace?: boolean }) => void;
    };
    organization?: string | undefined;
    config?: Partial<Auth0Config> | undefined;
}
/**
 * Auth0 Provider component with history/redirection support.
 *
 * This component wraps the Auth0Provider from @auth0/auth0-react and adds
 * custom redirect logic based on whether the user has already selected
 * an organization.
 *
 * @param props - Component props including router and optional organization
 * @returns Auth0Provider with custom redirect callback
 *
 * @example
 * ```tsx
 * const router = createBrowserRouter(routes);
 *
 * root.render(
 *   <Auth0ProviderWithHistory router={router} organization={orgId}>
 *     <RouterProvider router={router} />
 *   </Auth0ProviderWithHistory>
 * );
 * ```
 */
export const Auth0ProviderWithHistory: FC<Auth0ProviderWithHistoryProps> = ({
    children,
    router,
    organization,
    config: configOverrides,
}) => {
    // Get the complete Auth0 configuration
    const config = getAuth0Config(configOverrides);

    // Use custom hook to handle redirect logic
    const { onRedirectCallback } = useAuthRedirect({ router });

    // Build Auth0Provider props, only including organization if it's defined
    const auth0ProviderProps: import('@auth0/auth0-react').Auth0ProviderOptions =
        {
            domain: config.domain,
            clientId: config.clientId,
            authorizationParams: {
                redirect_uri: config.authorizationParams.redirect_uri,
                audience: config.authorizationParams.audience,
                scope: config.authorizationParams.scope,
            },
            useRefreshTokens: config.useRefreshTokens,
            cacheLocation: config.cacheLocation,
            onRedirectCallback,
            ...(organization !== undefined && { organization }),
        };

    return <Auth0Provider {...auth0ProviderProps}>{children}</Auth0Provider>;
};

export default Auth0ProviderWithHistory;
